import apiClient from './apiClient';

export interface User {
  id: string;
  email: string;
  subscription_tier: string;
  subscription_status: string;
  is_active: boolean;
  date_created: string | null;
  is_complimentary: boolean;
  roles: Array<{
    id: number;
    name: string;
    description: string | null;
    icon: string | null;
  }>;
  active_subscription: {
    subscription_id: string;
    paypal_plan_id: string;
    subscription_status: string;
    billing_cycle: string;
    tier: string;
  } | null;
}

export interface UserDetails extends User {
  complimentary_reason: string | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  subscription_cancelled_date: string | null;
  next_billing_date: string | null;
  paypal_subscription_id: string | null;
  paypal_email: string | null;
  roles: Array<{
    id: number;
    name: string;
    description: string | null;
    icon: string | null;
    is_active: boolean;
    granted_by: string | null;
    granted_at: string | null;
    valid_from: string | null;
    valid_until: string | null;
    user_role_id: number;
  }>;
  active_subscriptions: Array<{
    id: number;
    subscription_id: string;
    paypal_plan_id: string;
    tier: string;
    billing_cycle: string;
    subscription_status: string;
    is_current: boolean;
    is_active: boolean;
    created_at: string | null;
    updated_at: string | null;
  }>;
  subscription_history: Array<{
    id: number;
    event_type: string;
    subscription_id: string;
    tier: string;
    billing_cycle: string;
    created_at: string | null;
  }>;
}

export interface Role {
  id: number;
  name: string;
  description: string | null;
  icon: string | null;
  is_system_role: boolean;
  is_active: boolean;
}

export interface FamilyPlanForAdmin {
  id: number;
  owner_email: string | null;
  subscription_id: string;
  status: string;
  member_count: number;
  max_members: number;
}

export interface UsersResponse {
  users: User[];
  total: number;
  limit: number;
  offset: number;
}

export const adminUserService = {
  /**
   * Get all users with optional filtering, pagination, and sorting
   */
  getUsers: async (params?: {
    plan_id?: string;
    tier?: string;
    billing_cycle?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<UsersResponse> => {
    const response = await apiClient.get('/payment/admin/users', { params });
    return response.data.data;
  },

  /**
   * Get detailed information about a specific user
   */
  getUserDetails: async (userId: string): Promise<UserDetails> => {
    const response = await apiClient.get(`/payment/admin/users/${userId}`);
    return response.data.data;
  },

  /**
   * Get all active family plans for admin (e.g. when assigning Family Member role)
   */
  getFamilyPlans: async (): Promise<FamilyPlanForAdmin[]> => {
    const response = await apiClient.get('/payment/admin/family-plans');
    return response.data.data;
  },

  /**
   * Assign a role to a user
   * @param createFamilyPlan - When true and role is family, creates family plan (owner)
   * @param familyPlanId - When assigning family member, the family plan to add the user to
   */
  assignRole: async (
    userId: string,
    roleId: number,
    createFamilyPlan?: boolean,
    familyPlanId?: number
  ): Promise<void> => {
    const params: Record<string, string | number | boolean> = { role_id: roleId };
    if (createFamilyPlan !== undefined) params.create_family_plan = createFamilyPlan;
    if (familyPlanId !== undefined) params.family_plan_id = familyPlanId;
    await apiClient.post(`/payment/admin/users/${userId}/roles`, null, { params });
  },

  /**
   * Remove a role from a user
   */
  removeRole: async (userId: string, roleId: number): Promise<void> => {
    await apiClient.delete(`/payment/admin/users/${userId}/roles/${roleId}`);
  },

  /**
   * Get all available roles
   */
  getRoles: async (): Promise<Role[]> => {
    const response = await apiClient.get('/payment/admin/roles');
    return response.data.data;
  },

  /**
   * Sync user's subscription from PayPal API
   */
  syncUserSubscription: async (userId: string): Promise<any> => {
    const response = await apiClient.post(
      `/payment/admin/users/${userId}/subscription/sync`
    );
    return response.data.data;
  },

  /**
   * Get subscription history for a specific user (admin only)
   */
  getUserSubscriptionHistory: async (
    userId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<{
    events: Array<{
      id: number;
      date: string;
      event_type: string;
      event_source: string;
      description?: string;
      tier?: string;
      previous_tier?: string;
      status?: string;
      billing_cycle?: string;
      amount?: string;
      currency?: string;
      initiated_by?: string;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> => {
    const response = await apiClient.get(
      `/payment/admin/users/${userId}/subscription/history`,
      { params: { limit, offset } }
    );
    return response.data.data;
  },
};

